import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import {
  selectIsEditable,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { useAllowMessageSubmit } from '@verantos-dev/app-state-research-assistant';
import { useAutoOpenRA } from '@verantos-dev/feature-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

/**
 * Custom hook for cohort builder-specific Research Assistant behavior
 *
 * Handles cohort-specific logic such as:
 * - Auto-open RA when cohort is editable and not in view-only mode
 * - Close RA when leaving the cohort builder page
 *
 * Auto-open is suppressed under the `research_assistant_gen_3` flag — the
 * gen_3 sidebar hosts the epi-study-agent chat, which manages its own
 * open/close lifecycle and shouldn't be auto-closed when leaving the
 * cohort-builder page. The `isViewOnlyMode` projection is preserved either
 * way; callers depend on it for read-only chrome.
 */
export const useCohortBuilderResearchAssistant = () => {
  const isEditable = useCohortDefinitionStore(selectIsEditable);
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const allowMessageSubmit = useAllowMessageSubmit();
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false);

  const isCohortDataLoaded = !!latestStatus;
  const isViewOnlyMode =
    isCohortDataLoaded &&
    (!isEditable ||
      [CohortDefinitionStatus.FINAL, CohortDefinitionStatus.PROCESSING].includes(latestStatus));

  // Calculate when RA is ready to be opened
  const isRAReadyToOpen = Boolean(
    !isGen3Enabled && isCohortDataLoaded && !isViewOnlyMode && allowMessageSubmit
  );

  // Use the reusable auto-open RA hook
  useAutoOpenRA({
    readyToOpen: isRAReadyToOpen,
    closeOnUnmount: !isGen3Enabled, // Close legacy RA when leaving the page; gen_3 manages its own lifecycle
  });

  return { isViewOnlyMode };
};
